import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Check,
  Crown,
  DollarSign,
  ExternalLink,
  Minus,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { statsApi } from "@/lib/stats-api";
import { api } from "@/lib/api";
import TodayProgress from "@/components/RefClick/TodayProgress";
import ChargerIcon from "@/components/RefClick/ChargerIcon";
import SparkleBurst, { useSparkleTrigger } from "@/components/RefClick/SparkleBurst";
import { deriveMood, getReactionLadder } from "@/lib/moods";
import { useNow } from "@/components/RefClick/TickProvider";
import { serverToClientMs } from "@/lib/clock-sync";
import {
  copyToClipboard,
  formatElapsed,
  getDhakaHour,
  isSensitiveLabel,
  monogramStyle,
  monogramText,
} from "@/lib/refclick-utils";

// Max non-sensitive custom-field chips rendered directly on the card.
// Anything beyond this collapses into a single "+N" chip that opens
// the detail panel.
const MAX_CARD_CHIPS = 2;

/**
 * A single referral link — rendered as either a row (`variant="row"`) or
 * a card (`variant="card"`).
 *
 * Interaction map:
 *   - Clicking the card BODY (anywhere except the action cluster on the
 *     right) opens the LinkDetail slide-over via `onOpen`.
 *   - The action cluster stops propagation, so +1 / −1 / open-link /
 *     kebab menu keep their original behaviour untouched.
 *
 * Streak-saver:
 *   - After 20:00 Dhaka on a link whose target isn't met, we quietly add
 *     the `nudge-pulse` CSS animation to the heatmap dots. No popup, no
 *     toast — the pulse stops the moment the target is met.
 */
export default function LinkItem({
  link,
  earningsUsd = 0,
  avatarIndex = null,
  allLinks = [],
  isTopEarner = false,
  topEarnerInfo = null,
  variant = "row",
  shortcutHint = null,
  dragHandle = null,         // optional ReactNode — rendered as the GripVertical
  onClick,
  onUndo,
  onOpen,
  onEdit,
  onDelete,
  onEarningsChanged,
}) {
  const {
    id,
    name,
    url,
    daily_target: manualDailyTarget,
    // Feb-2026: server-computed effective target for TODAY. Falls back
    // to the manual value when the server didn't provide one (legacy
    // response shape or feature turned off — the backend still sets
    // effective_daily_target = manual in that case, so the fallback is
    // purely defensive).
    effective_daily_target: effectiveTarget,
    today_count: count,
    target_met: met,
    last_click_at: lastClickAt,
    last_opened_at: lastOpenedAt,
    last_7_days: last7,
    custom_fields: customFields = [],
  } = link;
  // Alias kept as `dailyTarget` so the existing render code (count "/N",
  // TodayProgress) keeps pointing at the number the user sees today.
  // Manual value (`manualDailyTarget`) is still accessible for the edit
  // form and any place that must show the unrolled config.
  const dailyTarget = Number.isFinite(effectiveTarget) && effectiveTarget > 0
    ? effectiveTarget
    : manualDailyTarget;

  /* --- Card-visible custom-field chips ---------------------------------
     Show up to MAX_CARD_CHIPS non-sensitive fields inline. Sensitive
     labels (password/pin/passcode/pass) are hidden — they only live in
     the detail panel behind the mask. Anything beyond the limit
     collapses into a single "+N" chip that opens the detail panel. */
  const visibleFields = useMemo(() => {
    // Server should always return an array (enrich_link normalizes),
    // but legacy docs may have dict/string. Coerce first so .filter
    // never runs on a non-array.
    const src = Array.isArray(customFields) ? customFields : [];
    return src.filter(
      (f) => f && (f.value || "").trim() && !isSensitiveLabel(f.label),
    );
  }, [customFields]);
  const shownFields = visibleFields.slice(0, MAX_CARD_CHIPS);
  const hiddenFieldCount = Math.max(0, visibleFields.length - shownFields.length);

  const handleChipCopy = async (e, field) => {
    e.stopPropagation();
    const ok = await copyToClipboard(field.value);
    if (ok) {
      toast.success(`Copied ${field.label}`);
    } else {
      toast.error("Copy failed");
    }
  };

  const handleChipMore = (e) => {
    // "+N" chip opens the detail panel to show all fields.
    e.stopPropagation();
    if (onOpen) onOpen(id);
  };

  /* --- Direction-aware count animation ------------------------------- */
  const prevCountRef = useRef(count);
  const dir =
    count > prevCountRef.current ? 1 : count < prevCountRef.current ? -1 : 0;
  useEffect(() => {
    prevCountRef.current = count;
  }, [count]);

  /* --- Live ticking "N seconds ago" ---------------------------------
     `now` re-renders every 1s via TickProvider. `optimisticClickMs`
     lets us reset the label to "0s ago" the instant the user hits +1,
     without waiting for the API round-trip / refresh. Once the server's
     freshly-refreshed last_click_at catches up we drop back to it. */
  const now = useNow();
  const [optimisticClickMs, setOptimisticClickMs] = useState(null);

  // Server timestamps arrive in the SERVER clock domain. Convert once
  // to the CLIENT clock domain (via clock-sync's measured offset) so
  // Math.max against the optimistic client-clock timestamp is
  // apples-to-apples and formatElapsed against Date.now() doesn't
  // suffer from server ↔ client clock skew.
  const serverClickMsRaw = lastClickAt ? new Date(lastClickAt).getTime() : 0;
  const serverClickMs = serverClickMsRaw
    ? serverToClientMs(serverClickMsRaw)
    : 0;

  // When today_count returns to 0 (last click undone), forget any
  // optimistic timestamp — otherwise the label would keep ticking
  // even though there's nothing to tick from.
  const effectiveClickMs =
    count === 0 ? 0 : Math.max(serverClickMs, optimisticClickMs || 0);

  useEffect(() => {
    // Drop the optimistic timestamp only once the server's (offset-
    // corrected) timestamp catches or surpasses it. Both operands are
    // now in the client clock domain, so this comparison is safe
    // regardless of the server↔client skew.
    if (optimisticClickMs && serverClickMs >= optimisticClickMs) {
      setOptimisticClickMs(null);
    }
  }, [serverClickMs, optimisticClickMs]);

  // Also clear the optimistic timestamp the moment today_count hits 0
  // (undo path). The next +1 gets a fresh one via handlePlus.
  useEffect(() => {
    if (count === 0 && optimisticClickMs) {
      setOptimisticClickMs(null);
    }
  }, [count, optimisticClickMs]);

  const elapsedLabel =
    count === 0
      ? null
      : formatElapsed(
          effectiveClickMs ? new Date(effectiveClickMs).toISOString() : null,
          now,
        );

  /* --- 30-minute "recently opened" countdown -----------------------
     When the user presses the ↗ open-in-new-tab button we stamp
     `last_opened_at` server-side. Here we compute the remaining
     seconds out of 30 minutes and render a small pill on the button.
     Server timestamp is converted through clock-sync so refresh /
     cross-device viewing agrees. Optimistic timestamp lets the pill
     appear instantly on click without waiting for the round-trip. */
  const OPENED_WINDOW_MS = 30 * 60 * 1000;
  const [optimisticOpenedMs, setOptimisticOpenedMs] = useState(null);
  const serverOpenedMsRaw = lastOpenedAt ? new Date(lastOpenedAt).getTime() : 0;
  const serverOpenedMs = serverOpenedMsRaw
    ? serverToClientMs(serverOpenedMsRaw)
    : 0;
  const effectiveOpenedMs = Math.max(serverOpenedMs, optimisticOpenedMs || 0);
  useEffect(() => {
    // Drop the optimistic stamp once the server-echoed one catches up.
    if (optimisticOpenedMs && serverOpenedMs >= optimisticOpenedMs) {
      setOptimisticOpenedMs(null);
    }
  }, [serverOpenedMs, optimisticOpenedMs]);
  const openedRemainMs = effectiveOpenedMs
    ? Math.max(0, OPENED_WINDOW_MS - (now - effectiveOpenedMs))
    : 0;
  const openedCountdownActive = openedRemainMs > 0;
  const openedRemainSec = Math.ceil(openedRemainMs / 1000);
  const openedLabel = openedCountdownActive
    ? `${String(Math.floor(openedRemainSec / 60)).padStart(2, "0")}:${String(
        openedRemainSec % 60,
      ).padStart(2, "0")}`
    : "";
  // Ring progress: fraction consumed (0 → 1 as the 30 min elapses).
  const openedRingPct = openedCountdownActive
    ? 1 - openedRemainMs / OPENED_WINDOW_MS
    : 0;

  const shouldNudge = !met && getDhakaHour() >= 20;

  /* --- Floating +1 / -1 particles + button ripple -------------------- */
  const [plusFloaters, setPlusFloaters] = useState([]);
  const [minusFloaters, setMinusFloaters] = useState([]);
  const [plusRipples, setPlusRipples] = useState([]);
  const [openRipples, setOpenRipples] = useState([]);

  const uid = () => Math.random().toString(36).slice(2);
  const pushWithCleanup = (setter, item, ttl = 900) => {
    setter((list) => [...list, item]);
    window.setTimeout(
      () => setter((list) => list.filter((x) => x.id !== item.id)),
      ttl,
    );
  };

  const handlePlus = (e) => {
    e.stopPropagation();
    const key = uid();
    pushWithCleanup(setPlusFloaters, { id: key });
    pushWithCleanup(setPlusRipples, { id: key }, 700);
    // Reset the "last click" label to 0s instantly — the server will
    // catch up on the next refresh.
    setOptimisticClickMs(Date.now());
    onClick();
  };
  const handleMinus = (e) => {
    e.stopPropagation();
    if (count > 0) pushWithCleanup(setMinusFloaters, { id: uid() });
    onUndo();
  };
  const handleOpenLink = (e) => {
    e.stopPropagation();
    pushWithCleanup(setOpenRipples, { id: uid() }, 550);
    // Optimistic stamp so the 30:00 pill appears instantly. Refresh /
    // cross-device consistency still come from the server's
    // authoritative last_opened_at.
    setOptimisticOpenedMs(Date.now());
    // Fire-and-forget POST. Failing to record MUST NOT block the tab
    // from opening — the anchor's default action runs regardless.
    api.markOpened(id).catch(() => {
      /* purely a UI hint; swallow errors */
    });
  };
  const handleCardActivate = () => {
    if (onOpen) onOpen(id);
  };
  const handleCardKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardActivate();
    }
  };

  const isCard = variant === "card";

  // Sparkle triggers — one per interactive control. Each hook memoizes
  // its own throttle so multiple bursts don't overlap on rapid hover.
  const plusSparkle = useSparkleTrigger();
  const minusSparkle = useSparkleTrigger();
  const openSparkle = useSparkleTrigger();

  // Mood + reaction (Feb-2026): the charger avatar reacts to today's
  // stats. `mood` derived from allLinks context; `reaction` is a
  // transient override fired on ±1 / target-met that expires after
  // 1500ms and returns control to the mood engine.
  const mood = deriveMood(link, allLinks);
  const [reaction, setReaction] = useState(null);
  const prevMetRef = useRef(met);
  useEffect(() => {
    // Detect count transitions to fire the reaction. Target-met wins
    // over the plain +1 reaction because it's the bigger celebration.
    // We piggyback on `dir` (already computed for the count-bump
    // animation) instead of tracking count again.
    //
    // Feb-2026 addition (reaction ladder): every +1 within the day
    // fires a DIFFERENT tier — plus1..plus6 — so 6 taps produce 6
    // visibly different emotions ending in maximum-hype "chillano".
    let next = null;
    if (met && !prevMetRef.current) next = "target";
    else if (dir > 0) next = getReactionLadder(count);
    else if (dir < 0) next = "minus";
    prevMetRef.current = met;
    if (!next) return undefined;
    setReaction(next);
    // Feb-2026: reaction window bumped 1500 → 3000ms per user request
    // ("longer animation durations, less rushed"). Count-pop, dots,
    // and ring feedback stay snappy — this only lengthens the FACE
    // swap so you actually see the emotion.
    const t = setTimeout(() => setReaction(null), 3000);
    return () => clearTimeout(t);
  }, [count, met, dir]);

  // prefers-reduced-motion — cached once. Feeds the undo-button
  // interactions so idle shimmer + wobble stay off for accessibility.
  const [reducedMotion] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    try {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      return false;
    }
  });

  return (
    <div
      data-testid={isCard ? "link-item-card" : "link-item-row"}
      data-link-id={id}
      role="button"
      tabIndex={0}
      onClick={handleCardActivate}
      onKeyDown={handleCardKey}
      className={
        (isCard
          ? "flex flex-col gap-3 rounded-xl border border-white/5 p-4 "
          : "flex flex-col gap-2 rounded-xl border border-transparent p-2 sm:flex-row sm:items-center sm:gap-3 sm:px-3 sm:py-2.5 ") +
        "focus-accent group cursor-pointer bg-white/[0.015] transition-colors hover:border-white/10 hover:bg-white/[0.04]"
      }
    >
      {/* Left — identity. Drag handle sits BEFORE the ChargerIcon so
          the whole-card click-to-expand still works everywhere else. */}
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        {dragHandle}
        <ChargerIcon
          name={name}
          count={count}
          target={dailyTarget}
          size={isCard ? 42 : 38}
          variant={isCard ? "card" : "row"}
          avatarIndex={avatarIndex}
          mood={mood}
          reaction={reaction}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <span
              className="truncate font-semibold text-text-main"
              data-testid="link-name"
              title={name}
            >
              {name}
            </span>
            <AnimatePresence>
              {isTopEarner && topEarnerInfo && (
                <motion.span
                  key="top-earner-ribbon"
                  initial={{ opacity: 0, scale: 0.7, y: -2 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.7 }}
                  transition={{ type: "spring", stiffness: 420, damping: 22 }}
                  data-testid="top-earner-ribbon"
                  data-usd-per-click={topEarnerInfo.usd_per_click_30d}
                  title={`Top earner this month · $${Number(topEarnerInfo.total_this_month || 0).toFixed(2)}`}
                  aria-label={`Top earner this month, ${Number(topEarnerInfo.total_this_month || 0).toFixed(2)} USD`}
                  className="inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold leading-none"
                  style={{
                    borderColor: "rgba(250, 204, 21, 0.55)",
                    background:
                      "linear-gradient(135deg, rgba(250, 204, 21, 0.28), rgba(250, 204, 21, 0.14))",
                    color: "#fde68a",
                    boxShadow: "0 0 8px rgba(250, 204, 21, 0.32)",
                  }}
                >
                  <Crown className="h-3 w-3" aria-hidden />
                  <span className="font-mono-num">
                    {(topEarnerInfo.usd_per_click_30d || 0) > 0
                      ? `$${Number(topEarnerInfo.usd_per_click_30d).toFixed(2)}/click`
                      : "top"}
                  </span>
                </motion.span>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {met && (
                <motion.span
                  key="met-badge"
                  initial={{ opacity: 0, scale: 0.6 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.6 }}
                  transition={{ duration: 0.25, ease: "backOut" }}
                  className="inline-flex items-center gap-0.5 rounded-full border border-accent/30 bg-accent/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent"
                  data-testid="link-met-badge"
                >
                  <Check className="h-3 w-3" />
                  Met
                </motion.span>
              )}
            </AnimatePresence>
          </div>

          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <motion.div
              key={count}
              initial={
                dir > 0
                  ? { scale: 1.55, y: -2, color: "rgb(var(--accent))" }
                  : dir < 0
                    ? { scale: 0.82, y: 3, color: "rgb(160 160 160)" }
                    : { scale: 1, y: 0 }
              }
              animate={{
                scale: 1,
                y: 0,
                color: met ? "rgb(var(--accent))" : "rgb(var(--text-main))",
              }}
              transition={{
                type: "spring",
                stiffness: 420,
                damping: 16,
                mass: 0.6,
              }}
              className="font-mono-num text-sm font-semibold leading-none"
              data-testid="link-count"
            >
              <span className={met ? "text-accent" : "text-text-main"}>
                {count}
              </span>
              <span className="text-text-muted">/{dailyTarget}</span>
            </motion.div>
            {earningsUsd > 0 && (
              <span
                data-testid="earnings-chip"
                data-total-usd={earningsUsd.toFixed(2)}
                className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-1.5 py-0.5 font-mono-num text-[10.5px] font-semibold leading-none text-text-muted"
                title={`Total earnings: $${earningsUsd.toFixed(2)}`}
              >
                <span className="text-text-muted/70">$</span>
                <span className="ml-0.5 text-accent">
                  {earningsUsd >= 1000
                    ? earningsUsd.toFixed(0)
                    : earningsUsd.toFixed(2)}
                </span>
              </span>
            )}
            <QuickAddEarning
              linkId={id}
              linkName={name}
              onAdded={onEarningsChanged}
            />
            <span
              className="truncate font-mono-safe text-[11px] leading-none text-text-muted"
              data-testid="link-last-click"
            >
              {elapsedLabel ? elapsedLabel : "—"}
            </span>
            {/* TodayProgress is now inline in the same metadata row —
                one line of "count · $ · quick+ · elapsed · dots" instead
                of a stacked block. Saves ~24px of vertical space per
                row and reads as a single glance-scannable strip. */}
            <TodayProgress
              count={count}
              target={dailyTarget}
              nudge={shouldNudge}
            />
          </div>

          {shownFields.length > 0 && (
            <div
              className="mt-1 flex flex-wrap items-center gap-1.5"
              data-testid="link-custom-chips"
            >
              {shownFields.map((f, i) => (
                <button
                  key={`${f.label}-${i}`}
                  type="button"
                  onClick={(e) => handleChipCopy(e, f)}
                  data-testid={`link-custom-chip-${i}`}
                  data-label={f.label}
                  title={`Copy ${f.label}: ${f.value}`}
                  className="focus-accent inline-flex max-w-[16rem] items-center gap-1 rounded-full border border-white/8 bg-white/[0.03] px-2 py-0.5 font-mono-safe text-[10.5px] leading-none text-text-muted transition-colors hover:border-accent/30 hover:bg-accent/10 hover:text-text-main"
                >
                  <span className="shrink-0 uppercase tracking-wider text-text-muted/80">
                    {f.label}:
                  </span>
                  <span className="truncate text-text-main/85">{f.value}</span>
                </button>
              ))}
              {hiddenFieldCount > 0 && (
                <button
                  type="button"
                  onClick={handleChipMore}
                  data-testid="link-custom-chip-more"
                  aria-label={`Show ${hiddenFieldCount} more fields`}
                  title={`+${hiddenFieldCount} more · open details`}
                  className="focus-accent inline-flex items-center rounded-full border border-white/8 bg-white/[0.03] px-2 py-0.5 font-mono-num text-[10.5px] leading-none text-text-muted transition-colors hover:border-accent/30 hover:bg-accent/10 hover:text-text-main"
                >
                  +{hiddenFieldCount}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right — actions. This wrapper stops the card-click from firing on
          any of the buttons inside so their existing handlers stay
          isolated.
          Feb-2026 spacing pass: bumped from gap-2 → gap-2.5 across the
          cluster, then added an extra ml-1.5 gap between the −1 and +1
          buttons specifically (was ~8px, now ~14-16px including the −1
          circle's own margin). Two side-effects reinforce it visually:
            * −1 is now a rounded-FULL circle (was a square), so the two
              buttons no longer share a silhouette at a glance.
            * +1 keeps its rectangular keycap. Circle ↔ rectangle is
              unmistakable even without reading the icon. */}
      <div
        className={
          "flex items-center gap-2.5 sm:gap-3 " +
          (isCard ? "mt-1" : "shrink-0")
        }
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {/* Open-in-new-tab — quick press ripple, no count change */}
        <div className="relative flex flex-col items-center">
          {/* Sparkle overlay rendered as sibling so it escapes the
              button's overflow-hidden clip AND is stacked ABOVE it
              via z-30 (Feb-2026 fix: without z-index the button was
              masking most of the burst area on hover). Wider inset
              so particles bloom clearly outside the button rim. */}
          <span className="pointer-events-none absolute -inset-4 z-30">
            <SparkleBurst trigger={openSparkle.count} variant="star" />
          </span>
          <motion.a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleOpenLink}
            onPointerEnter={openSparkle.onEnter}
            onPointerDown={openSparkle.onPress}
            whileTap={{ scale: 0.88 }}
            whileHover={{ y: -1 }}
            transition={{ type: "spring", stiffness: 500, damping: 22 }}
            data-testid="open-link-btn"
            aria-label={`Open ${name} in a new tab`}
            className="focus-accent relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-md text-text-muted transition-colors hover:bg-white/10 hover:text-accent active:bg-white/10 sm:h-9 sm:w-9"
          >
            <ExternalLink className="h-4 w-4" />
            <AnimatePresence>
              {openRipples.map((r) => (
                <motion.span
                  key={r.id}
                  initial={{ opacity: 0.45, scale: 0.2 }}
                  animate={{ opacity: 0, scale: 2.6 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="pointer-events-none absolute inset-0 rounded-full bg-white/25"
                />
              ))}
            </AnimatePresence>
            {/* Depleting ring around the icon: only rendered while
                the 30-minute window is active. SVG stroke-dasharray
                is animated smoothly by the useNow tick. Uses the theme's
                accent colour for visibility across all skins. Purely
                informational — never blocks the open action. */}
            {openedCountdownActive && (
              <svg
                className="pointer-events-none absolute inset-0 h-full w-full -rotate-90"
                viewBox="0 0 36 36"
                aria-hidden
                data-testid="open-countdown-ring"
              >
                <circle
                  cx="18"
                  cy="18"
                  r="15"
                  fill="none"
                  stroke="currentColor"
                  strokeOpacity="0.12"
                  strokeWidth="2"
                />
                <circle
                  cx="18"
                  cy="18"
                  r="15"
                  fill="none"
                  className="text-accent"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 15}`}
                  strokeDashoffset={`${2 * Math.PI * 15 * openedRingPct}`}
                  style={{ transition: "stroke-dashoffset 1s linear" }}
                />
              </svg>
            )}
          </motion.a>
          {/* mm:ss pill under the button — only during the window. Kept
              tiny + muted so it doesn't compete with the +1 keycap. */}
          {openedCountdownActive && (
            <span
              data-testid="open-countdown-label"
              aria-label={`Opened ${openedLabel} ago window`}
              className="pointer-events-none absolute -bottom-4 whitespace-nowrap rounded-full bg-white/[0.06] px-1.5 py-[1px] font-mono-num text-[9px] leading-none text-accent/90"
            >
              {openedLabel}
            </span>
          )}
        </div>

        {/* −1 undo — regenerated (Feb-2026 batch #5 + polish pass #6):
            Now a CIRCLE (rounded-full) so it never gets mistaken for
            the rectangular +1 keycap next to it. Adds a red-tint
            flash on press: an inset rose-500/45 overlay pulses in +
            out over ~260ms, making the tap unmistakable even in a
            silent screen recording. Idle shimmer only when count > 0
            (there's something to undo); everything transform/opacity
            only for 60fps, prefers-reduced-motion respected. */}
        <div className="relative">
          {/* Sparkle overlay rendered as a SIBLING of the button (not
              a child) so the button's overflow-hidden — needed for
              the ripple + red-tint flash — doesn't clip the sparkle
              particles that translate outside the button's bounds.
              Feb-2026 fix v2: -inset-4 pushes points fully clear of
              the button's own rose-glow ring; explicit z-30 lifts
              them ABOVE the button so they can't be masked by its
              gradient background; variant="star" matches +1 so all
              three action buttons share the same sparkle language. */}
          <span className="pointer-events-none absolute -inset-4 z-30">
            <SparkleBurst trigger={minusSparkle.count} />
          </span>
          <motion.button
            type="button"
            onClick={handleMinus}
            onPointerEnter={minusSparkle.onEnter}
            onPointerDown={minusSparkle.onPress}
            whileTap={
              reducedMotion
                ? { scale: 0.92 }
                : { scale: 0.82, rotate: -10, y: 2 }
            }
            whileHover={
              reducedMotion || count === 0
                ? undefined
                : {
                    y: -1,
                    boxShadow:
                      "0 6px 20px -8px rgb(244, 63, 94, 0.55), 0 0 0 1px rgb(244, 63, 94, 0.35)",
                  }
            }
            transition={{ type: "spring", stiffness: 520, damping: 20, mass: 0.4 }}
            data-testid="minus-one-btn"
            data-active={count > 0 ? "true" : "false"}
            aria-label="Undo one click"
            aria-disabled={count === 0 ? "true" : "false"}
            className={
              "focus-accent group/undo relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border border-white/12 " +
              "bg-gradient-to-b from-white/[0.10] via-white/[0.04] to-white/[0.015] " +
              "text-text-muted shadow-[inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-1px_0_rgba(0,0,0,0.35)] " +
              "transition-colors hover:border-rose-400/55 hover:text-rose-200 active:border-rose-400/80 " +
              "sm:h-10 sm:w-10 " +
              (count === 0 ? "opacity-55" : "opacity-100")
            }
          >
            {/* Top-highlight arc — mimics the rim highlight of a
                physical circular key. */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-x-2 top-0 h-px rounded-full bg-gradient-to-r from-transparent via-white/40 to-transparent"
            />
            {/* Idle shimmer — sweeps only when count > 0. Kept faint. */}
            {count > 0 && !reducedMotion && (
              <motion.span
                aria-hidden
                className="pointer-events-none absolute -inset-y-2 -left-1/2 w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/12 to-transparent"
                animate={{ x: ["-40%", "220%"] }}
                transition={{
                  duration: 3.6,
                  ease: "easeInOut",
                  repeat: Infinity,
                  repeatDelay: 1.4,
                }}
              />
            )}
            <Minus
              className="relative z-10 h-4 w-4 transition-transform duration-150 group-active/undo:-rotate-12"
              strokeWidth={2.8}
            />
            {/* Red-tint flash on press — the unmistakable "you just
                pressed undo" signal. Keyed off minusFloaters so it
                fires exactly once per handleMinus call, whether or
                not count is > 0. */}
            <AnimatePresence>
              {minusFloaters.map((f) => (
                <motion.span
                  key={`tint-${f.id}`}
                  aria-hidden
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 0.75, 0] }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
                  className="pointer-events-none absolute inset-0 rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle at 50% 50%, rgba(244, 63, 94, 0.55) 0%, rgba(244, 63, 94, 0.15) 55%, transparent 80%)",
                  }}
                />
              ))}
            </AnimatePresence>
            {/* Release ring — expanding rose outline once the tap
                lands. Reinforces the flash with a physical "shockwave"
                shape so the animation reads even at 15fps recordings. */}
            <AnimatePresence>
              {minusFloaters.map((f) => (
                <motion.span
                  key={`ring-${f.id}`}
                  aria-hidden
                  initial={{ opacity: 0.7, scale: 0.35 }}
                  animate={{ opacity: 0, scale: 2.2 }}
                  transition={{ duration: 0.55, ease: "easeOut" }}
                  className="pointer-events-none absolute inset-0 rounded-full border-2 border-rose-400/70"
                />
              ))}
            </AnimatePresence>
          </motion.button>
          <AnimatePresence>
            {minusFloaters.map((f) => (
              <motion.span
                key={f.id}
                initial={{ opacity: 0, y: 0, scale: 0.7 }}
                animate={{ opacity: [0, 1, 0], y: -34, scale: [0.7, 1.05, 0.95] }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="pointer-events-none absolute -top-3 left-0 right-0 text-center font-mono-num text-xs font-bold text-rose-300"
                style={{ textShadow: "0 0 10px rgba(244, 63, 94, 0.6)" }}
              >
                −1
              </motion.span>
            ))}
          </AnimatePresence>
        </div>

        {/* +1 keycap — CSS keycap press, framer floaters + ripple */}
        <div className="relative">
          {/* Sparkle overlay hoisted outside the overflow-hidden
              keycap so particles that translate upward stay visible.
              Feb-2026 fix v2: -inset-4 + z-30 for consistency with
              the other action buttons; guarantees particles bloom
              clearly regardless of the keycap's own bright bg. */}
          <span className="pointer-events-none absolute -inset-4 z-30">
            <SparkleBurst trigger={plusSparkle.count} variant="star" />
          </span>
          <button
            type="button"
            onClick={handlePlus}
            onPointerEnter={plusSparkle.onEnter}
            onPointerDown={plusSparkle.onPress}
            data-testid="plus-one-btn"
            aria-label={`Add one click to ${name}`}
            className="keycap focus-accent relative inline-flex min-h-[44px] items-center gap-1 overflow-hidden rounded-lg px-5 py-2 font-mono-num text-base font-bold text-accent sm:min-h-0 sm:px-4 sm:text-sm"
          >
            <span className="relative z-10">+1</span>
            {/* Keyboard shortcut hint badges removed from the +1 button on
                2026-10-15 per user feedback ("button icon fix koro —
                12345 pichone keno?"). The full shortcut map stays
                accessible via the "?" overlay (ShortcutsOverlay.jsx). */}
            <AnimatePresence>
              {plusRipples.map((r) => (
                <motion.span
                  key={r.id}
                  initial={{ opacity: 0.55, scale: 0.15 }}
                  animate={{ opacity: 0, scale: 3 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="pointer-events-none absolute inset-0 m-auto h-full w-full rounded-full bg-white/40"
                />
              ))}
            </AnimatePresence>
          </button>

          <AnimatePresence>
            {plusFloaters.map((f) => (
              <motion.span
                key={f.id}
                initial={{ opacity: 0, y: 0, scale: 0.7 }}
                animate={{ opacity: [0, 1, 1, 0], y: -46, scale: [0.7, 1.1, 1, 0.95] }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="pointer-events-none absolute -top-3 left-0 right-0 text-center font-mono-num text-sm font-extrabold text-accent"
                style={{ textShadow: "0 0 12px rgb(var(--accent) / 0.9)" }}
              >
                +1
              </motion.span>
            ))}
          </AnimatePresence>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="link-menu-trigger"
              aria-label="More actions"
              className="focus-accent flex h-10 w-10 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-white/10 hover:text-text-main active:bg-white/15 sm:h-9 sm:w-9"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="border-white/10 bg-[#141416]/95 text-text-main backdrop-blur-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenuItem
              onClick={onEdit}
              data-testid="link-edit-action"
              className="gap-2 focus:bg-white/5 focus:text-text-main"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={onDelete}
              data-testid="link-delete-action"
              className="gap-2 text-red-300 focus:bg-red-500/10 focus:text-red-200"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* QuickAddEarning — small "+$" popover glued next to the $-chip.   */
/* Saves as a normal MANUAL earning dated today. Same validation as */
/* the detail-panel earnings form (positive, ≤ $1,000,000, note ≤   */
/* 200 chars). onAdded is fired so the parent can refresh totals.   */
/* ---------------------------------------------------------------- */

/* Trigger button — hoisted + forwardRef so Radix `asChild` can wire
   its own ref + `data-state` + click handler on top of ours. Owns
   its own sparkle trigger for hover / press glint. */
const QuickEarnTriggerButton = React.forwardRef(function QuickEarnTriggerButton(
  props,
  ref,
) {
  const sparkle = useSparkleTrigger();
  const { onPointerEnter, onPointerDown, ...rest } = props;
  const chain = (a, b) => (e) => {
    a(e);
    if (b) b(e);
  };
  return (
    <button
      ref={ref}
      type="button"
      onClick={(e) => e.stopPropagation()}
      onPointerEnter={chain(sparkle.onEnter, onPointerEnter)}
      onPointerDown={chain(sparkle.onPress, onPointerDown)}
      data-testid="quick-earn-open"
      aria-label="Add earning"
      title="Add a $ earning for this link"
      className="focus-accent relative inline-flex h-6 items-center gap-0.5 overflow-visible rounded-full border border-white/10 bg-white/[0.03] px-1.5 font-mono-num text-[10px] font-semibold text-text-muted transition-colors hover:border-accent/40 hover:bg-accent/10 hover:text-accent"
      {...rest}
    >
      <Plus className="h-3 w-3" />
      <DollarSign className="h-3 w-3" />
      <SparkleBurst trigger={sparkle.count} />
    </button>
  );
});

function QuickAddEarning({ linkId, linkName, onAdded }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Same parsing rules as EarningsSection so validation feels consistent
  // between the quick popover and the full form in the detail panel.
  const parsedAmount = useMemo(() => {
    if (!amount || amount.trim() === "") return null;
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0 || n > 1_000_000) return null;
    return n;
  }, [amount]);
  const canAdd = parsedAmount != null && !saving;

  const reset = () => {
    setAmount("");
    setNote("");
  };

  const handleAdd = async (e) => {
    if (e) e.preventDefault();
    if (!canAdd) return;
    setSaving(true);
    try {
      await statsApi.addEarning(linkId, {
        amount_usd: parsedAmount,
        note: note.trim() || undefined,
      });
      toast.success(`Logged $${parsedAmount.toFixed(2)} · ${linkName}`);
      reset();
      setOpen(false);
      onAdded && onAdded();
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        (typeof err?.response?.data?.detail === "string"
          ? err.response.data.detail
          : "Couldn't save earning");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
    >
      <PopoverTrigger asChild>
        <QuickEarnTriggerButton />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        data-testid="quick-earn-popover"
        onClick={(e) => e.stopPropagation()}
        onOpenAutoFocus={(e) => {
          // Focus the amount field on open — the user's flow is
          // "tap +$, type a number, hit Enter".
          const input = e.currentTarget.querySelector(
            "[data-testid='quick-earn-amount']",
          );
          if (input) {
            e.preventDefault();
            input.focus();
          }
        }}
        className="w-64 border-white/10 bg-[#0a0a0c] p-3 text-text-main"
      >
        <div className="mb-2 flex items-baseline justify-between">
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-text-muted">
            Quick add earning
          </span>
          <span
            className="max-w-[130px] truncate font-mono-safe text-[10px] text-text-muted"
            title={linkName}
          >
            {linkName}
          </span>
        </div>
        <form onSubmit={handleAdd} className="flex flex-col gap-2">
          <div className="relative">
            <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted/70">
              <DollarSign className="h-3.5 w-3.5" />
            </span>
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              inputMode="decimal"
              autoComplete="off"
              disabled={saving}
              data-testid="quick-earn-amount"
              className="h-9 border-white/10 bg-black/40 pl-8 font-mono-num text-text-main placeholder:text-text-muted/40 focus-visible:border-accent focus-visible:ring-accent/50"
            />
          </div>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional)"
            maxLength={200}
            disabled={saving}
            data-testid="quick-earn-note"
            className="h-9 border-white/10 bg-black/40 text-text-main placeholder:text-text-muted/40 focus-visible:border-accent focus-visible:ring-accent/50"
          />
          <Button
            type="submit"
            disabled={!canAdd}
            data-testid="quick-earn-submit"
            className="h-8 w-full border border-accent/40 bg-accent/20 text-xs font-semibold text-accent hover:bg-accent/30 disabled:opacity-40"
          >
            {saving
              ? "Adding…"
              : parsedAmount != null
                ? `Add $${parsedAmount.toFixed(2)}`
                : "Add earning"}
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}
